import { FC } from 'react'
import { Link } from 'react-router-dom'
import { RetroButton } from '@/components/retro'

export const PrivacyScreen: FC = () => {
  return (
    <div className="min-h-screen bg-game-bg p-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-pixel text-xl text-game-accent">PRIVACY POLICY</h1>
          <Link to="/">
            <RetroButton variant="default" size="sm">BACK</RetroButton>
          </Link>
        </div>

        {/* Content */}
        <div className="border-2 border-game-border bg-game-panel p-6 space-y-6">
          <p className="font-retro text-sm text-game-muted">
            Last Updated: January 30, 2025
          </p>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">1. INTRODUCTION</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              Trench Quest ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our application.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">2. INFORMATION WE COLLECT</h2>
            <div className="space-y-2">
              <h3 className="font-pixel text-sm text-game-text">Wallet Information</h3>
              <p className="font-retro text-base text-game-text leading-relaxed">
                When you connect your Solana wallet, we collect your public wallet address. We do NOT have access to your private keys or seed phrases. Your wallet connection is used solely for authentication and game progress tracking.
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-pixel text-sm text-game-text">Game Data</h3>
              <p className="font-retro text-base text-game-text leading-relaxed">
                We store your game progress, including portfolio state, game choices, achievements, and session data. This data is associated with your wallet address.
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-pixel text-sm text-game-text">Usage Data</h3>
              <p className="font-retro text-base text-game-text leading-relaxed">
                We may collect anonymous usage statistics to improve the game experience, including session duration, features used, and error logs.
              </p>
            </div>
            <div className="space-y-2">
              <h3 className="font-pixel text-sm text-game-text">Optional Social Data</h3>
              <p className="font-retro text-base text-game-text leading-relaxed">
                If you choose to connect your Twitter/X account, we collect your public profile information for social features like sharing achievements. This is entirely optional.
              </p>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">3. HOW WE USE YOUR INFORMATION</h2>
            <ul className="font-retro text-base text-game-text leading-relaxed list-disc list-inside space-y-1">
              <li>To provide and maintain the game service</li>
              <li>To save and restore your game progress</li>
              <li>To authenticate your identity via wallet signature</li>
              <li>To enable optional social features</li>
              <li>To improve and optimize the game experience</li>
              <li>To detect and prevent fraud or abuse</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">4. DATA STORAGE AND SECURITY</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              Your data is stored securely using Google Firebase services. We implement industry-standard security measures to protect your information. However, no method of transmission over the Internet is 100% secure.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">5. DATA SHARING</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              We do NOT sell, trade, or rent your personal information to third parties. We may share anonymous, aggregated data for analytics purposes. We may disclose information if required by law or to protect our rights.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">6. YOUR RIGHTS</h2>
            <ul className="font-retro text-base text-game-text leading-relaxed list-disc list-inside space-y-1">
              <li>Access your personal data</li>
              <li>Request deletion of your data</li>
              <li>Disconnect your wallet at any time</li>
              <li>Opt out of optional data collection</li>
            </ul>
            <p className="font-retro text-base text-game-text leading-relaxed">
              To exercise these rights, contact us at privacy@trenchquest.com
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">7. COOKIES AND LOCAL STORAGE</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              We use browser local storage to save preferences and cache game data for performance. You can clear this data through your browser settings.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">8. CHILDREN'S PRIVACY</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              Trench Quest is intended for users 18 years of age or older. We do not knowingly collect information from children under 18. If you believe we have collected such information, please contact us immediately.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">9. CHANGES TO THIS POLICY</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify users of any material changes by updating the "Last Updated" date. Continued use of the app after changes constitutes acceptance of the new policy.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-pixel text-base text-game-accent">10. CONTACT US</h2>
            <p className="font-retro text-base text-game-text leading-relaxed">
              If you have questions about this Privacy Policy, please contact us at:
            </p>
            <p className="font-retro text-base text-game-accent">
              privacy@trenchquest.com
            </p>
          </section>

          {/* Footer */}
          <div className="pt-6 border-t border-game-border text-center">
            <p className="font-retro text-sm text-game-muted">
              Trench Quest - A Solana Trading Adventure
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
